import { useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged, 
  User 
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase/config';

interface UserData {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string;
  createdAt: any;
  lastLoginAt: any;
  totalWordsLearned: number;
  currentStreak: number;
  longestStreak: number;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        await loadUserData(user.uid);
      } else {
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const loadUserData = async (uid: string) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        setUserData(userDoc.data() as UserData);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Kullanıcı verilerini kontrol et/oluştur
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        // Yeni kullanıcı oluştur
        const newUserData: UserData = {
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          createdAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
          totalWordsLearned: 0,
          currentStreak: 0,
          longestStreak: 0,
        };
        
        await setDoc(userDocRef, newUserData);
        setUserData(newUserData);
      } else {
        // Mevcut kullanıcının son giriş zamanını güncelle
        await updateDoc(userDocRef, {
          lastLoginAt: serverTimestamp(),
        });
        setUserData(userDoc.data() as UserData);
      }
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const signOutUser = async () => {
    try {
      await signOut(auth);
      setUserData(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const updateUserProgress = async (totalWordsLearned: number, currentStreak: number) => {
    if (!user) return;
    
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const currentData = userDoc.data() as UserData;
        const longestStreak = Math.max(currentData.longestStreak, currentStreak);
        
        await updateDoc(userDocRef, {
          totalWordsLearned,
          currentStreak,
          longestStreak,
        });
        
        setUserData({
          ...currentData,
          totalWordsLearned,
          currentStreak,
          longestStreak,
        });
      }
    } catch (error) {
      console.error('Error updating user progress:', error);
    }
  };

  return {
    user,
    userData,
    loading,
    signInWithGoogle,
    signOutUser,
    updateUserProgress,
  };
};
